import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PaymentRecord from "./PaymentRecord";
import {
	TextField,
	Button,
	Typography,
	Box,
	Paper,
	IconButton,
	Autocomplete,
	FormGroup,
	FormControlLabel,
	Switch
} from "@mui/material";
import Grid from "@mui/material/Grid";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HomeIcon from "@mui/icons-material/Home";
import { auth } from "../../firebase/firebaseConfig";
import { auditedSaveInvoice } from "../../firebase/auditLog/wrappedOperations";
import { updateItemAvailableQuantity } from "../../firebase/itemsAndInventory/updateItemAvailableQuantity";
import { InvoiceData, LineItem } from "../../models/invoice";
import { generateInvoiceNumber } from "../../firebase/invoice/generateInvoiceNumber";
import { savePayment } from "../../firebase/payment/savePayment";
import { PaymentData } from "../../models/payment";
import { formatDate, getTodayUTCDateString } from "../../utils/calculations";
import { RootState, AppDispatch } from "../../redux/store";
import { fetchItems } from "../../redux/slices/itemsSlice";
import { Item } from "../../redux/slices/itemsSlice";
import { getItemById } from "../../firebase/itemsAndInventory/getItemById";
import { AlertDialog } from "../Shared/AlertDialog";
import { useAuth } from "../../context/AuthContext";

export const InvoiceForm = () => {
	const navigate = useNavigate();
	const [alertDialogOpen, setAlertDialogOpen] = useState(false);
	const [alertDialogContent, setAlertDialogContent] = useState("");
	const dispatch = useDispatch<AppDispatch>();
	const [customerName, setCustomerName] = useState("");
	const [mobileNumber, setMobileNumber] = useState("");
	const [items, setItems] = useState<LineItem[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [successMsg, setSuccessMsg] = useState<string | null>(null);
	const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
	const [pendingInvoiceNumber, setPendingInvoiceNumber] =
		useState<string>("");
	const [payments, setPayments] = useState<PaymentData[]>([]);
	const [itemKeyInput, setItemKeyInput] = useState<string>("");
	const [isAutocomplete, setIsAutocomplete] = useState(false);
	const [showHiddenFields, setShowHiddenFields] = useState(false);

	// Get items from Redux store
	const allItems = useSelector((state: RootState) => state.items.items);
	const { userRole } = useAuth();
	const isAdminUser = userRole === "admin";
	const shouldEnforceProfitMargin = !(isAdminUser && showHiddenFields);

	const calculateProfit = (purchase: number, sell: number): number => {
		if (purchase === 0) return 0;
		return parseFloat((((sell - purchase) / purchase) * 100).toFixed(2));
	};

	const getItemProfitPercent = (item: LineItem): number => {
		return calculateProfit(item.purchasePrice, item.sellPrice);
	};

	const isItemBelowProfitMargin = (item: LineItem) => {
		if (item.purchasePrice <= 0) return false;
		return getItemProfitPercent(item) < item.minProfitPercent;
	};

	const handleAddItem = (item: Item | null | undefined) => {
		if (!item) return;
		if (item.availableQuantity <= 0) {
			setAlertDialogContent(`Item "${item.name}" is out of stock.`);
			setAlertDialogOpen(true);
			return;
		}
		setItems((prev) => {
			const existingItemIndex = prev.findIndex(
				(existingItem) => existingItem.itemKey === item.key
			);

			if (existingItemIndex !== -1) {
				// If item row exists, increase quantity (use immutable update to avoid accidental double-mutation)
				return prev.map((existingItem, index) =>
					index === existingItemIndex
						? {
								...existingItem,
								quantity: existingItem.quantity + 1
							}
						: existingItem
				);
			} else {
				// If item does not exist, add it to the list
				return [
					...prev,
					{
						description: item.name,
						purchasePrice: item.purchasePrice,
						sellPrice: item.sellPrice,
						quantity: 1,
						lineItemId: crypto.randomUUID(),
						itemKey: item.key,
						itemCode: item.itemCode,
						minProfitPercent: item.minProfitPercent
					}
				];
			}
		});
	};

	const handleItemChange = (
		index: number,
		field: keyof LineItem,
		value: string | number
	) => {
		const updatedItems = [...items];
		if (
			field === "purchasePrice" ||
			field === "sellPrice" ||
			field === "quantity"
		) {
			// ensure numeric fields are set as numbers
			updatedItems[index] = {
				...updatedItems[index],
				[field]: Number(value) || 0
			};
		} else {
			// description is a string
			updatedItems[index] = {
				...updatedItems[index],
				[field]: String(value)
			};
		}
		setItems(updatedItems);
	};

	const handleDeleteItem = (index: number) => {
		setItems((prev) => prev.filter((_, i) => i !== index));
	};

	// Show Payment modal first
	const handleRecordPayment = async () => {
		setSuccessMsg(null);
		if (!pendingInvoiceNumber) {
			const invoiceNumber = await generateInvoiceNumber();
			setPendingInvoiceNumber(invoiceNumber);
		}
		setPaymentDialogOpen(true);
	};

	// Called after payment is recorded
	const handlePaymentSave = (payment: PaymentData) => {
		setPaymentDialogOpen(false);
		setPayments((prev) => [...prev, payment]);
	};

	// Remove payment record
	const handleRemovePayment = (idx: number) => {
		setPayments((prev) => prev.filter((_, i) => i !== idx));
	};

	// Save invoice and all payments
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSuccessMsg(null);

		if (!pendingInvoiceNumber.trim()) {
			alert(
				"Invoice number is required before saving. Please record a payment first."
			);
			return;
		}
		if (items.length === 0) {
			alert("Please add at least one item before saving the invoice.");
			return;
		}

		const hasOutstandingPayment = totalPayments < totalSell;
		if (
			hasOutstandingPayment &&
			(!customerName.trim() || !mobileNumber.trim())
		) {
			alert(
				"Customer name and mobile number are required for outstanding payment."
			);
			return;
		}
		setIsSubmitting(true);
		const user = auth.currentUser;
		if (!user) {
			setIsSubmitting(false);
			return;
		}
		const invoice: InvoiceData = {
			invoiceNumber: pendingInvoiceNumber.trim(),
			customerName: customerName.trim(),
			mobileNumber: mobileNumber.trim(),
			items,
			createdAt: getTodayUTCDateString(),
			userId: user.uid,
			username: user.email || "Unknown"
		};
		try {
			// Save invoice (audit errors won't block invoice persistence)
			try {
				await auditedSaveInvoice(invoice);
			} catch (err) {
				console.error("Failed to save invoice:", err);
				throw err; // still rethrow so outer catch handles UI
			}
			// Save payments individually; don't fail the whole flow if one payment errors
			for (const payment of payments) {
				if (payment.amount > 0) {
					try {
						await savePayment(payment);
					} catch (err) {
						console.error("Failed to save payment:", err, payment);
						// show user but continue with other saves
						alert(
							`Failed to save payment of ₹${payment.amount}. Check console for details.`
						);
					}
				}
			}
			// update items available quantity in inventory
			for (const item of items) {
				const inventoryItem = await getItemById(item.itemKey);
				if (inventoryItem) {
					const newAvailableQuantity =
						(inventoryItem.availableQuantity || 0) - item.quantity;
					await updateItemAvailableQuantity(
						item.itemKey,
						newAvailableQuantity
					);
				}
			}
			// Refresh items in Redux store after invoice creation
			dispatch(fetchItems());

			setSuccessMsg(
				`Invoice saved successfully! Invoice Number: ${invoice.invoiceNumber}`
			);
		} catch (err) {
			alert("Failed to save invoice");
		} finally {
			setIsSubmitting(false);
			setCustomerName("");
			setMobileNumber("");
			setItems([]);
			setPendingInvoiceNumber("");
			setPayments([]);
		}
	};

	const totalPurchase = items.reduce(
		(sum, item) => sum + item.purchasePrice * item.quantity,
		0
	);
	const totalSell = items.reduce(
		(sum, item) => sum + item.sellPrice * item.quantity,
		0
	);
	const grossProfitPercent =
		totalPurchase === 0
			? 0
			: (((totalSell - totalPurchase) / totalPurchase) * 100).toFixed(2);
	const grossProfitAmount = (totalSell - totalPurchase).toFixed(2);

	const totalPayments = payments.reduce(
		(sum, p) => sum + Number(p.amount),
		0
	);

	// Check if any line item's sell price's profit is less than the configured minimum profit percentage.
	// Admin users with hidden fields enabled can bypass this validation.
	const hasLowSellPrice = shouldEnforceProfitMargin
		? items.some(isItemBelowProfitMargin)
		: false;

	return (
		<Paper elevation={3} sx={{ p: 3, mt: 3 }}>
			<AlertDialog
				isOpen={alertDialogOpen}
				title="Out of Stock"
				content={alertDialogContent}
				onClose={() => setAlertDialogOpen(false)}
			/>
			{successMsg && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						bgcolor: "success.light",
						color: "success.contrastText",
						p: 1,
						borderRadius: 1,
						mb: 2
					}}
					role="alert"
				>
					<Typography variant="body1">{successMsg}</Typography>
					<IconButton
						aria-label="close"
						size="small"
						onClick={() => setSuccessMsg(null)}
						sx={{ color: "inherit" }}
					>
						×
					</IconButton>
				</Box>
			)}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 2,
					gap: 1
				}}
			>
				<Typography variant="h5">Create Invoice</Typography>
				<Button
					variant="contained"
					startIcon={<HomeIcon />}
					onClick={() => navigate("/")}
				>
					Go to Home
				</Button>
			</Box>
			<Grid container spacing={2}>
				<Grid size={{ xs: 12, sm: 6 }}>
					<TextField
						label="Customer Name (optional)"
						value={customerName}
						onChange={(e) => setCustomerName(e.target.value)}
						fullWidth
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6 }}>
					<TextField
						label="Mobile Number (optional)"
						value={mobileNumber}
						onChange={(e) => setMobileNumber(e.target.value)}
						inputProps={{ pattern: "[0-9]{10}", maxLength: 10 }}
						placeholder="10-digit number"
						fullWidth
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6 }}>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									checked={isAutocomplete}
									onChange={() =>
										setIsAutocomplete(!isAutocomplete)
									}
								/>
							}
							label="Autocomplete"
						/>
					</FormGroup>
				</Grid>
				<Grid size={{ xs: 12, sm: 6 }}>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									checked={showHiddenFields}
									onChange={() =>
										setShowHiddenFields(!showHiddenFields)
									}
								/>
							}
							label="Show Hidden Fields"
						/>
					</FormGroup>
				</Grid>
				<Grid size={12}>
					{isAutocomplete ? (
						<Autocomplete
							autoSelect
							options={allItems}
							getOptionLabel={(option: Item) =>
								`(${option.itemCode}) ${option.name}` ||
								option.key
							}
							onChange={(event, newValue) => {
								handleAddItem(newValue);
							}}
							filterOptions={(options, { inputValue }) => {
								return options.filter(
									(option) =>
										option.name
											.toLowerCase()
											.includes(
												inputValue.toLowerCase()
											) ||
										option.itemCode
											.toLowerCase()
											.includes(inputValue.toLowerCase())
								);
							}}
							renderInput={(params) => (
								<TextField {...params} label="Select an item" />
							)}
						/>
					) : (
						<TextField
							label="Enter Or Scan Item Code"
							value={itemKeyInput}
							onChange={(e) => setItemKeyInput(e.target.value)}
							onKeyDown={(
								e: React.KeyboardEvent<HTMLInputElement>
							) => {
								if (e.key === "Enter" && !e.repeat) {
									e.preventDefault();
									if (itemKeyInput.trim() === "") return;
									let itemCode = itemKeyInput.trim();
									if (!itemCode.startsWith("FB-")) {
										itemCode = `FB-${itemCode}`;
									}
									handleAddItem(
										allItems.find(
											(item) => item.itemCode === itemCode
										)
									);
									setItemKeyInput("");
								}
							}}
							placeholder="e.g. FB-001"
							fullWidth
						/>
					)}
				</Grid>
			</Grid>

			{items.length > 0 && (
				<Box sx={{ mt: 3 }}>
					<Typography variant="h6">
						<span style={{ fontSize: 20 }}>🛒</span>Cart
					</Typography>
					{items.map((item, index) => {
						const isLowSell = shouldEnforceProfitMargin
							? isItemBelowProfitMargin(item)
							: false;
						const lineTotal = item.sellPrice * item.quantity;
						return (
							<Paper
								key={index}
								sx={{
									mb: 2,
									p: 2,
									display: "flex",
									flexDirection: { xs: "column", md: "row" },
									alignItems: { xs: "stretch", md: "center" },
									gap: 2,
									border: isLowSell
										? "2px solid #ffa726"
										: undefined
								}}
							>
								<TextField
									label="Description"
									value={`(${item.itemCode})  ${item.description}`}
									disabled={true}
									onChange={(e) =>
										handleItemChange(
											index,
											"description",
											e.target.value
										)
									}
									sx={{ flex: 2 }}
								/>
								<TextField
									label="Purchase Price"
									type="number"
									value={item.purchasePrice}
									onChange={(e) =>
										handleItemChange(
											index,
											"purchasePrice",
											e.target.value
										)
									}
									sx={{ flex: 1 }}
									InputProps={{ readOnly: true }}
									disabled={true}
									hidden={!showHiddenFields}
								/>
								<TextField
									label="Sell Price"
									type="number"
									value={item.sellPrice}
									onChange={(e) =>
										handleItemChange(
											index,
											"sellPrice",
											e.target.value
										)
									}
									sx={{ flex: 1 }}
								/>
								<TextField
									label="Quantity"
									type="number"
									value={item.quantity}
									onChange={(e) =>
										handleItemChange(
											index,
											"quantity",
											e.target.value
										)
									}
									sx={{ flex: 1 }}
									inputProps={{ min: 1 }}
								/>
								<TextField
									label="Total"
									type="number"
									value={lineTotal}
									sx={{ flex: 1 }}
									InputProps={{ readOnly: true }}
									disabled={true}
								/>
								<Typography
									sx={{ flex: 1 }}
									hidden={!showHiddenFields}
								>
									Profit :₹
									{(item.sellPrice - item.purchasePrice) *
										item.quantity}{" "}
									({getItemProfitPercent(item)}
									%)
								</Typography>
								{isLowSell && (
									<WarningAmberIcon
										color="warning"
										titleAccess="Sell price is less than 30% of purchase price"
									/>
								)}
								<IconButton
									color="error"
									onClick={() => handleDeleteItem(index)}
									sx={{
										alignSelf: {
											xs: "flex-end",
											md: "center"
										}
									}}
								>
									<DeleteIcon />
								</IconButton>
							</Paper>
						);
					})}
					<Box sx={{ mt: 2, fontWeight: "bold" }}>
						<Typography
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								mb: 1
							}}
							hidden={!showHiddenFields}
						>
							<span style={{ fontSize: 20 }}>💵</span>
							Total Purchase: ₹{totalPurchase.toFixed(2)}
						</Typography>
						<Typography
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								mb: 1
							}}
						>
							<span style={{ fontSize: 20 }}>💰</span>
							Total Sell: ₹{totalSell.toFixed(2)}
						</Typography>
						<Typography
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1
							}}
							hidden={!showHiddenFields}
						>
							<span style={{ fontSize: 20 }}>📈</span>
							Gross Profit: ₹{grossProfitAmount} (
							{grossProfitPercent}%)
						</Typography>
					</Box>
				</Box>
			)}

			{/* Record Payment Button and Modal */}
			{items.length > 0 && totalPayments < totalSell && (
				<Button
					variant="contained"
					color="primary"
					sx={{ mt: 3, width: { xs: "100%", sm: "auto" } }}
					onClick={handleRecordPayment}
					disabled={hasLowSellPrice || isSubmitting}
				>
					Record Payment
				</Button>
			)}
			{paymentDialogOpen && (
				<PaymentRecord
					invoiceNumber={pendingInvoiceNumber}
					open={paymentDialogOpen}
					onClose={() => setPaymentDialogOpen(false)}
					// validate payment amount before saving to avoid over-payments
					onSave={(payment) => {
						const remaining = Number(
							(totalSell - totalPayments).toFixed(2)
						);
						const amt = Number(payment.amount);
						if (amt < 0) {
							alert("Payment can't be negative.");
							return;
						}
						if (amt > remaining) {
							alert(
								`Payment cannot be greater than remaining amount (₹${remaining.toFixed(
									2
								)}).`
							);
							return;
						}
						handlePaymentSave(payment);
					}}
					// default to remaining amount (never negative)
					defaultAmount={Math.max(
						0,
						Number((totalSell - totalPayments).toFixed(2))
					)}
				/>
			)}

			{/* Show payment details after recording */}
			{payments.length > 0 && (
				<Box
					sx={{
						mt: 3,
						mb: 2,
						p: 2,
						bgcolor: "grey.200",
						borderRadius: 2
					}}
				>
					<Typography variant="h6">Payment Records 💵 </Typography>
					<table className="table table-bordered table-striped">
						<thead>
							<tr>
								<th>Amount</th>
								<th>Mode</th>
								<th>Date</th>
								<th>Remove</th>
							</tr>
						</thead>
						<tbody>
							{payments.map((payment, idx) => (
								<tr key={idx}>
									<td>
										₹{Number(payment.amount).toFixed(2)}
									</td>
									<td>{payment.mode}</td>
									<td>
										{payment.paymentDate
											? formatDate(payment.paymentDate)
											: "—"}
									</td>
									<td>
										<IconButton
											color="error"
											onClick={() =>
												handleRemovePayment(idx)
											}
										>
											<DeleteIcon />
										</IconButton>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<Typography sx={{ mt: 1 }}>
						Total Payments: ₹{totalPayments.toFixed(2)} / ₹
						{totalSell.toFixed(2)}
					</Typography>
					{totalPayments > totalSell && (
						<Typography color="error" sx={{ mt: 1 }}>
							Total payments exceed total sell. Remove or correct
							payment(s).
						</Typography>
					)}
				</Box>
			)}

			{/* Allow saving even with partial payments, but prevent over-payments */}
			{payments.length > 0 && totalPayments <= totalSell && (
				<form
					onSubmit={(e) => {
						if (totalPayments > totalSell) {
							e.preventDefault();
							alert(
								"Total payments exceed total sell amount. Please correct payments."
							);
							return;
						}
						handleSubmit(e);
					}}
				>
					<Button
						type="submit"
						variant="contained"
						color="success"
						sx={{ mt: 2, width: { xs: "100%", sm: "auto" } }}
						disabled={isSubmitting}
					>
						{isSubmitting ? "Saving..." : "Save Invoice"}
					</Button>
				</form>
			)}
		</Paper>
	);
};
